/**
 * FieldError Component - Field-Specific Error Display
 * 
 * Displays validation errors for individual form fields with consistent styling
 * Supports error codes, icons, and accessibility features
 * 
 * Features:
 * - Consistent error styling across the application
 * - Error code display for debugging
 * - Accessibility support with ARIA attributes
 * - Icon support for visual feedback
 * - Responsive design
 * 
 * Usage:
 * <FieldError error={fieldErrors.taxRate} />
 * <FieldError error={{ message: "Required field", code: "REQUIRED" }} />
 */

import React from 'react';
import PropTypes from 'prop-types';

/**
 * FieldError component for displaying validation errors
 * 
 * @param {Object} props - Component props
 * @param {Object|null} props.error - Error object with message and code
 * @param {boolean} props.showCode - Whether to show error code (default: false in production)
 * @param {string} props.className - Additional CSS classes
 * @param {boolean} props.showIcon - Whether to show error icon (default: true)
 * @returns {JSX.Element|null} Error component or null if no error
 */
const FieldError = ({ 
  error = null, 
  showCode = process.env.NODE_ENV === 'development', 
  className = '', 
  showIcon = true 
}) => {
  // Don't render if no error
  if (!error || !error.message) {
    return null;
  }

  /**
   * Get error icon based on error code
   * @param {string} code - Error code
   * @returns {JSX.Element} SVG icon
   */
  const getErrorIcon = (code) => {
    const iconProps = {
      className: "w-4 h-4 mr-2 flex-shrink-0",
      fill: "currentColor",
      viewBox: "0 0 20 20",
      'aria-hidden': "true"
    };

    // Different icons for different error types
    switch (code) {
      case 'REQUIRED':
      case 'CODE_REQUIRED':
      case 'TITLE_REQUIRED':
      case 'METHOD_REQUIRED':
      case 'VALUE_REQUIRED':
        return (
          <svg {...iconProps}>
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
        );
      
      case 'INVALID_FORMAT':
      case 'INVALID_TYPE':
        return (
          <svg {...iconProps}>
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
          </svg>
        );
      
      case 'INVALID_RANGE':
        return (
          <svg {...iconProps}>
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
        );
      
      case 'BUSINESS_RULE_VIOLATION':
      case 'CONSTRAINT_VIOLATION':
      case 'CODE_IN_USE':
      case 'CODE_NOT_FOUND':
      case 'CODE_INACTIVE':
      case 'CODE_NOT_STARTED':
      case 'CODE_EXPIRED':
      case 'CODE_NOT_APPLICABLE':
      case 'CODE_COMBINATION':
        return (
          <svg {...iconProps}>
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
        );
      
      default:
        // Generic warning icon
        return (
          <svg {...iconProps}>
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
        );
    }
  };

  /**
   * Get error severity class based on error code
   * @param {string} code - Error code
   * @returns {string} CSS class for severity
   */
  const getSeverityClass = (code) => {
    switch (code) {
      case 'REQUIRED':
      case 'CODE_REQUIRED':
      case 'TITLE_REQUIRED':
      case 'METHOD_REQUIRED':
      case 'VALUE_REQUIRED':
        return 'text-red-700';
      case 'INVALID_FORMAT':
      case 'INVALID_TYPE':
      case 'INVALID_RANGE':
        return 'text-red-600';
      case 'BUSINESS_RULE_VIOLATION':
      case 'CONSTRAINT_VIOLATION':
      case 'CODE_IN_USE':
        return 'text-red-800';
      case 'CLIENT_VALIDATION':
        return 'text-orange-600';
      default:
        return 'text-red-600';
    }
  };

  const errorCode = error.code || 'VALIDATION_ERROR';
  const severityClass = getSeverityClass(errorCode);
  const baseClasses = `mt-1 text-sm flex items-start ${severityClass}`;
  const combinedClasses = `${baseClasses} ${className}`.trim();

  return (
    <div 
      className={combinedClasses}
      role="alert"
      aria-live="polite"
      data-testid={`field-error-${errorCode.toLowerCase()}`}
    >
      {showIcon && getErrorIcon(errorCode)}
      <div className="flex-1">
        <span className="font-medium">
          {error.message}
        </span>
        
        {showCode && error.code && (
          <span className="ml-2 text-xs opacity-75 font-mono">
            [{error.code}]
          </span>
        )}
        
        {/* Show original message if different and in development */}
        {process.env.NODE_ENV === 'development' && 
         error.originalMessage && 
         error.originalMessage !== error.message && (
          <div className="text-xs opacity-60 mt-1 font-mono">
            Original: {error.originalMessage}
          </div>
        )}
      </div>
    </div>
  );
};

FieldError.propTypes = {
  error: PropTypes.shape({
    message: PropTypes.string.isRequired,
    code: PropTypes.string,
    originalMessage: PropTypes.string
  }),
  showCode: PropTypes.bool,
  className: PropTypes.string,
  showIcon: PropTypes.bool
};

// defaultProps replaced with JavaScript default parameters above

/**
 * GeneralErrors component for displaying multiple general errors
 * 
 * @param {Object} props - Component props
 * @param {Array} props.errors - Array of error objects
 * @param {string} props.className - Additional CSS classes
 * @returns {JSX.Element|null} Errors component or null if no errors
 */
export const GeneralErrors = ({ errors = [], className = '' }) => {
  if (!errors.length) {
    return null;
  }

  const baseClasses = "mb-4 space-y-2";
  const combinedClasses = `${baseClasses} ${className}`.trim();

  return (
    <div className={combinedClasses}>
      {errors.map((error, index) => (
        <div
          key={index}
          className="bg-red-50 border border-red-200 rounded-md p-4"
          role="alert"
          aria-live="polite"
        >
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="w-5 h-5 text-red-400" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">
                {error.message}
              </h3>
              {process.env.NODE_ENV === 'development' && error.code && (
                <p className="mt-1 text-xs text-red-700 font-mono">
                  Error Code: {error.code}
                </p>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

GeneralErrors.propTypes = {
  errors: PropTypes.arrayOf(PropTypes.shape({
    message: PropTypes.string.isRequired,
    code: PropTypes.string
  })),
  className: PropTypes.string
};

export default FieldError;